import React from 'react'
import {
    Card,
    Col,
    Descriptions,
    Empty,
    Image,
    Row,
    Space,
    Typography,
} from 'antd'
import { useTranslation } from 'react-i18next'

import {
    HistoryLabelOption,
    HistoryPrediction,
    HistoryRecord,
} from '../../../types/history'
import {
    getMosquitoCategoryDisplayName,
    getPredictionDisplayCategory,
    sortMosquitoDisplayCategories,
    type MosquitoDisplayCategory,
} from '../../../utils/mosquitoDisplay'
import {
    BASIC_DEVICE_BOX_COLOR,
    shouldUseBasicBoxOnlyHistoryImage,
} from '../../../utils/historyCapabilities'
import { getRegionPath } from '../../../services/regionService'
import { getImageUrl } from '../../../utils/urlHelper'
import { resolveHistoryDisplayPredictions } from '../../../utils/historyPredictions'
import { formatRawClassDisplayName } from '../../../utils/rawClassDisplay'
import {
    getManualRecognitionTypeDisplayName,
    projectPredictionsToManualRecognitionTypes,
    resolveManualRecognitionTypeOption,
} from '../../../utils/manualRecognitionDisplay'
import { ReadonlyDetectionOverlayImage } from '../../common/ReadonlyDetectionOverlayImage'
import { ImagePreviewMaskContent } from '../../common/ImagePreviewHoverMask'
import { DetectionAnnotationEditor } from './DetectionAnnotationEditor'
import type {
    AnnotationActionPlacement,
    AnnotationFooterActions,
    AnnotationZoomControlPlacement,
} from './DetectionAnnotationEditor'

const { Text } = Typography

const normalizeModelTypeKey = (value?: string | null): string =>
    typeof value === 'string' ? value.trim().toLowerCase() : ''

const getRawModelTypeIdentity = (prediction: HistoryPrediction): string => {
    const modelKey = normalizeModelTypeKey(prediction.model_key) || 'mosquito'
    const rawClass = normalizeModelTypeKey(
        prediction.raw_class_norm ||
            prediction.raw_class ||
            prediction.eng_class ||
            prediction.class,
    )
    return rawClass ? `${modelKey}:${rawClass}` : ''
}

const getRawModelTypeLabel = (prediction: HistoryPrediction): string =>
    prediction.raw_class ||
    prediction.raw_class_norm ||
    prediction.eng_class ||
    prediction.class ||
    '-'

const getRawModelOptionIdentity = (option: HistoryLabelOption): string => {
    if (option.identity_key) {
        return normalizeModelTypeKey(option.identity_key)
    }
    const modelKey = normalizeModelTypeKey(option.model_key) || 'mosquito'
    const rawClass = normalizeModelTypeKey(
        option.raw_class_norm || option.raw_class || option.eng_class,
    )
    return rawClass ? `${modelKey}:${rawClass}` : ''
}

const formatNameWithCode = (code: string, displayName: string): string => {
    const normalizedCode = code.trim()
    const normalizedDisplayName = displayName.trim()
    if (!normalizedCode) {
        return normalizedDisplayName
    }
    if (!normalizedDisplayName || normalizedDisplayName === normalizedCode) {
        return normalizedCode
    }
    return `${normalizedCode} · ${normalizedDisplayName}`
}

const getManualRecognitionTypeReadonlyDisplayName = (
    option: HistoryLabelOption,
    language?: string,
): string => {
    const displayName = getManualRecognitionTypeDisplayName(option, language)
    const code = option.manual_type_key || option.eng_class || option.class
    return formatNameWithCode(code, displayName)
}

interface DetailMetricCardProps {
    label: string
    value: React.ReactNode
    suffix?: React.ReactNode
}

interface ImageResultViewProps {
    record: HistoryRecord
    editable: boolean
    annotationSeedPredictions: HistoryPrediction[]
    referencePredictions?: HistoryPrediction[]
    annotationSeedKey: string
    annotationDirty: boolean
    annotationLabelOptions?: HistoryLabelOption[]
    annotationActionPlacement?: AnnotationActionPlacement
    zoomControlPlacement?: AnnotationZoomControlPlacement
    defaultPredictionConfidence?: number
    readonlyPredictionSource?: 'model' | 'effective'
    bypassCapabilityFallback?: boolean
    useManualRecognitionTypes?: boolean
    readonlyPreviewOnImageClick?: boolean
    showReadonlyDetectionInfo?: boolean
    showAnnotationLabelsOnBoxes?: boolean
    showAnnotationTypeLegend?: boolean
    rawClassLocalizedNames?: Record<string, string>
    onPredictionsChange: (predictions: HistoryPrediction[]) => void
    onAnnotationFooterActionsChange?: (
        actions: AnnotationFooterActions | null,
    ) => void
    onResetPredictions: () => void
    formatDate: (timestamp: number) => string
}

export const ImageResultView: React.FC<ImageResultViewProps> = ({
    record,
    editable,
    annotationSeedPredictions,
    referencePredictions,
    annotationSeedKey,
    annotationDirty,
    annotationLabelOptions,
    annotationActionPlacement,
    zoomControlPlacement,
    defaultPredictionConfidence,
    readonlyPredictionSource = 'model',
    bypassCapabilityFallback = false,
    useManualRecognitionTypes = false,
    readonlyPreviewOnImageClick = false,
    showReadonlyDetectionInfo = true,
    showAnnotationLabelsOnBoxes = false,
    showAnnotationTypeLegend = false,
    rawClassLocalizedNames,
    onPredictionsChange,
    onAnnotationFooterActionsChange,
    onResetPredictions,
    formatDate,
}) => {
    const { t, i18n } = useTranslation('history')
    const result = record.result
    const usesRawModelTypes =
        Boolean(record.annotation_context) && !useManualRecognitionTypes
    const sourceImage = result.source_image || record.thumbnail
    const fallbackImage = result.annotated_image || record.thumbnail
    const [annotatedImageLoadFailed, setAnnotatedImageLoadFailed] =
        React.useState(false)
    const [loadedReadonlyImage, setLoadedReadonlyImage] = React.useState<
        string | null
    >(null)
    const [regionPathLabel, setRegionPathLabel] = React.useState('-')
    React.useEffect(() => {
        let cancelled = false

        const loadRegionPath = async () => {
            const snapshotRegionPath = record.region_path
                ?.map((region) => region.name)
                .filter((name) => name && name.trim())
                .join(' / ')
            if (snapshotRegionPath) {
                setRegionPathLabel(snapshotRegionPath)
                return
            }

            if (!record.region_id) {
                if (!cancelled) {
                    setRegionPathLabel('-')
                }
                return
            }

            try {
                const path = await getRegionPath(record.region_id)
                if (cancelled) {
                    return
                }

                const label = path.map((region) => region.name).join(' / ')
                setRegionPathLabel(label || '-')
            } catch {
                if (!cancelled) {
                    setRegionPathLabel('-')
                }
            }
        }

        void loadRegionPath()

        return () => {
            cancelled = true
        }
    }, [record.region_id, record.region_path])

    const manualTypeOptions = record.manual_type_options ?? []
    const displayLanguage = i18n?.resolvedLanguage || i18n?.language
    const readonlyManualTypeOptions = manualTypeOptions.map((option) => ({
        ...option,
        display_name: getManualRecognitionTypeReadonlyDisplayName(
            option,
            displayLanguage,
        ),
    }))
    const modelPredictions = resolveHistoryDisplayPredictions(
        result,
        useManualRecognitionTypes ? false : bypassCapabilityFallback,
    )
    const manualRecognitionPredictions = record.manual_recognition?.predictions
    const hasManualRecognitionPayload = Array.isArray(
        manualRecognitionPredictions,
    )
    const hasSavedAnnotationPredictions =
        usesRawModelTypes && Array.isArray(record.review?.predictions)
    const hasRecordEffectivePredictions =
        readonlyPredictionSource === 'effective' &&
        Array.isArray(record.effective_predictions)
    const hasManualEffectivePredictions =
        readonlyPredictionSource === 'effective' &&
        ((hasRecordEffectivePredictions &&
            record.effective_source === 'manual') ||
            hasManualRecognitionPayload)
    const sourcePredictions = hasSavedAnnotationPredictions
        ? (record.review?.predictions ?? [])
        : hasRecordEffectivePredictions
          ? (record.effective_predictions ?? [])
          : hasManualEffectivePredictions
            ? (manualRecognitionPredictions ?? [])
            : modelPredictions
    const predictions = useManualRecognitionTypes
        ? projectPredictionsToManualRecognitionTypes(
              sourcePredictions,
              readonlyManualTypeOptions,
              displayLanguage,
          )
        : sourcePredictions
    const manualEffectiveAnnotatedImage = hasManualEffectivePredictions
        ? record.effective_annotated_image ||
          record.manual_recognition?.annotated_image ||
          null
        : null
    const readonlyAnnotatedImage =
        manualEffectiveAnnotatedImage ||
        result.annotated_image ||
        record.thumbnail ||
        null
    const showManualReadonlyOverlay =
        !editable &&
        hasManualEffectivePredictions &&
        !manualEffectiveAnnotatedImage
    const showBasicBoxOnlyPreview =
        !editable &&
        !usesRawModelTypes &&
        !useManualRecognitionTypes &&
        shouldUseBasicBoxOnlyHistoryImage(record) &&
        Boolean(result.source_image)
    const showModelReadonlyOverlay =
        !editable &&
        hasRecordEffectivePredictions &&
        record.effective_source === 'model'
    const showReadonlyOverlay =
        showManualReadonlyOverlay ||
        showModelReadonlyOverlay ||
        (!editable && usesRawModelTypes && Boolean(sourceImage)) ||
        (!editable &&
            (useManualRecognitionTypes || readonlyPreviewOnImageClick) &&
            Boolean(sourceImage))

    React.useEffect(() => {
        setAnnotatedImageLoadFailed(false)
        setLoadedReadonlyImage(null)
    }, [readonlyAnnotatedImage])

    const deviceLabel =
        record.device_name ||
        record.device_id ||
        t('messages.deviceUnavailable')
    const visiblePredictions = usesRawModelTypes
        ? predictions
        : useManualRecognitionTypes
          ? predictions.filter((prediction) =>
                resolveManualRecognitionTypeOption(
                    prediction,
                    readonlyManualTypeOptions,
                ),
            )
          : predictions.filter(getPredictionDisplayCategory)
    const visibleManualTypeKeys = new Set(
        visiblePredictions
            .map((prediction) => prediction.manual_type_key)
            .filter((key): key is string => Boolean(key)),
    )
    const recognitionLabelOptions =
        record.annotation_context?.recognition_label_options
    const annotationModelOptions = record.annotation_context?.model_options
    const recognitionLabelOptionsByIdentity = React.useMemo(() => {
        const optionsByIdentity = new Map<string, HistoryLabelOption>()
        const mergeLabelOption = (option: HistoryLabelOption) => {
            const identityKey = getRawModelOptionIdentity(option)
            if (!identityKey) {
                return
            }
            const current = optionsByIdentity.get(identityKey)
            optionsByIdentity.set(identityKey, {
                ...current,
                ...option,
                color: option.color || current?.color,
            })
        }

        recognitionLabelOptions?.forEach(mergeLabelOption)
        annotationModelOptions?.forEach((modelOption) => {
            modelOption.label_options.forEach(mergeLabelOption)
        })

        return optionsByIdentity
    }, [annotationModelOptions, recognitionLabelOptions])
    const recognitionColorByRawClass = React.useMemo(() => {
        const colorsByRawClass = new Map<string, string>()
        recognitionLabelOptionsByIdentity.forEach((option) => {
            const rawClass = normalizeModelTypeKey(
                option.raw_class_norm || option.raw_class || option.eng_class,
            )
            if (rawClass && option.color && !colorsByRawClass.has(rawClass)) {
                colorsByRawClass.set(rawClass, option.color)
            }
        })
        return colorsByRawClass
    }, [recognitionLabelOptionsByIdentity])
    const readonlyRawLabelOptions = visiblePredictions.reduce<
        HistoryLabelOption[]
    >((options, prediction) => {
        const identityKey = getRawModelTypeIdentity(prediction)
        if (
            !identityKey ||
            options.some(
                (option) => getRawModelOptionIdentity(option) === identityKey,
            )
        ) {
            return options
        }
        const configuredOption =
            recognitionLabelOptionsByIdentity.get(identityKey)
        const rawClass = getRawModelTypeLabel(prediction)
        const rawClassNorm = normalizeModelTypeKey(
            prediction.raw_class_norm ||
                prediction.raw_class ||
                prediction.eng_class ||
                prediction.class,
        )
        options.push({
            ...(configuredOption ?? {}),
            identity_key: identityKey,
            model_key:
                configuredOption?.model_key ||
                prediction.model_key ||
                'mosquito',
            raw_class: configuredOption?.raw_class || rawClass,
            raw_class_norm: configuredOption?.raw_class_norm || rawClassNorm,
            cls_id:
                configuredOption?.cls_id ?? prediction.cls_id ?? options.length,
            eng_class: configuredOption?.eng_class || rawClass,
            class: configuredOption?.class || rawClass,
            display_name: formatRawClassDisplayName(
                rawClass,
                t,
                rawClassLocalizedNames?.[rawClassNorm] ||
                    configuredOption?.display_name,
            ),
            color:
                configuredOption?.color ||
                recognitionColorByRawClass.get(rawClassNorm),
        })
        return options
    }, [])
    const readonlyLegendPredictions =
        showAnnotationTypeLegend && !useManualRecognitionTypes
            ? predictions.filter(getPredictionDisplayCategory)
            : visiblePredictions
    const readonlyLegendCategories = readonlyLegendPredictions
        .map(getPredictionDisplayCategory)
        .filter(
            (category): category is MosquitoDisplayCategory =>
                category !== null,
        )
    const hasReadonlyRawModelTypeEvidence = visiblePredictions.some(
        (prediction) =>
            Boolean(prediction.raw_class_norm || prediction.raw_class),
    )
    const configuredReadonlyLabelOptions: HistoryLabelOption[] =
        usesRawModelTypes && hasReadonlyRawModelTypeEvidence
            ? readonlyRawLabelOptions
            : sortMosquitoDisplayCategories(
                  new Set(readonlyLegendCategories),
              ).map((category, index) => {
                  const prediction = readonlyLegendPredictions.find(
                      (item) => getPredictionDisplayCategory(item) === category,
                  )
                  return {
                      cls_id: index,
                      eng_class: prediction?.display_category_key || category,
                      class: category,
                      display_category_key:
                          prediction?.display_category_key || category,
                      display_name: getMosquitoCategoryDisplayName(
                          category,
                          t,
                          'detail.detectionClasses',
                      ),
                      color: prediction?.color,
                  }
              })
    const readonlyDisplayLabelOptions = useManualRecognitionTypes
        ? readonlyManualTypeOptions.filter((option) => {
              const manualTypeKey = option.manual_type_key
              return Boolean(
                  manualTypeKey && visibleManualTypeKeys.has(manualTypeKey),
              )
          })
        : configuredReadonlyLabelOptions
    const localizedAnnotationLabelOptions = annotationLabelOptions?.map(
        (option) => {
            const rawClass =
                option.raw_class ||
                option.raw_class_norm ||
                option.eng_class ||
                option.class
            return {
                ...option,
                display_name: formatRawClassDisplayName(
                    rawClass,
                    t,
                    rawClassLocalizedNames?.[
                        normalizeModelTypeKey(rawClass)
                    ] || option.display_name,
                ),
            }
        },
    )
    const editableDisplayLabelOptions = React.useMemo(
        () =>
            useManualRecognitionTypes
                ? readonlyManualTypeOptions
                : localizedAnnotationLabelOptions
                  ? localizedAnnotationLabelOptions
                  : record.manual_type_options?.length
                    ? record.manual_type_options
                    : result.label_options || [],
        [
            localizedAnnotationLabelOptions,
            readonlyManualTypeOptions,
            record.manual_type_options,
            result.label_options,
            useManualRecognitionTypes,
        ],
    )
    const annotationTypeLegendPredictions = editable
        ? annotationSeedPredictions
        : predictions
    const editableFallbackLabelOptions = React.useMemo<
        HistoryLabelOption[]
    >(() => {
        if (!showAnnotationTypeLegend || useManualRecognitionTypes) {
            return []
        }

        return annotationTypeLegendPredictions.reduce<HistoryLabelOption[]>(
            (options, prediction) => {
                const identityKey = getRawModelTypeIdentity(prediction)
                if (
                    !identityKey ||
                    editableDisplayLabelOptions.some(
                        (option) =>
                            getRawModelOptionIdentity(option) === identityKey,
                    ) ||
                    options.some(
                        (option) =>
                            getRawModelOptionIdentity(option) === identityKey,
                    )
                ) {
                    return options
                }

                const configuredOption =
                    recognitionLabelOptionsByIdentity.get(identityKey)
                const rawClass = getRawModelTypeLabel(prediction)
                const rawClassNorm = normalizeModelTypeKey(
                    prediction.raw_class_norm ||
                        prediction.raw_class ||
                        prediction.eng_class ||
                        prediction.class,
                )
                options.push({
                    ...(configuredOption ?? {}),
                    identity_key: identityKey,
                    model_key:
                        configuredOption?.model_key ||
                        prediction.model_key ||
                        'mosquito',
                    raw_class: configuredOption?.raw_class || rawClass,
                    raw_class_norm:
                        configuredOption?.raw_class_norm || rawClassNorm,
                    cls_id:
                        configuredOption?.cls_id ??
                        prediction.cls_id ??
                        options.length,
                    eng_class: configuredOption?.eng_class || rawClass,
                    class: configuredOption?.class || rawClass,
                    display_name: formatRawClassDisplayName(
                            rawClass,
                            t,
                            rawClassLocalizedNames?.[rawClassNorm] ||
                                configuredOption?.display_name,
                    ),
                    color:
                        configuredOption?.color ||
                        recognitionColorByRawClass.get(rawClassNorm),
                })
                return options
            },
            [],
        )
    }, [
        annotationTypeLegendPredictions,
        editableDisplayLabelOptions,
        recognitionColorByRawClass,
        recognitionLabelOptionsByIdentity,
        rawClassLocalizedNames,
        showAnnotationTypeLegend,
        t,
        useManualRecognitionTypes,
    ])
    const annotationEditorLabelOptions = showAnnotationTypeLegend
        ? [...editableDisplayLabelOptions, ...editableFallbackLabelOptions]
        : editableDisplayLabelOptions
    const targetCount = visiblePredictions.length
    const imagePreviewEmptyDescription = record.media_expired
        ? t('detail.imagePreviewExpired')
        : annotatedImageLoadFailed
          ? t('detail.imagePreviewLoadFailed')
          : t('detail.imagePreviewUnavailable')
    const metricTargetCount = targetCount
    const metricTargetUnit = t('detail.targetUnit')
    const metricDuration = result.time_cost ?? '-'
    const metricDurationUnit =
        typeof result.time_cost === 'number'
            ? t('detail.durationUnit')
            : undefined

    const metricCardStyle: React.CSSProperties = {
        height: '100%',
        borderRadius: 8,
        background: '#f8fafc',
    }

    const renderMetricCard = ({
        label,
        value,
        suffix,
    }: DetailMetricCardProps) => (
        <Card
            size="small"
            styles={{ body: { padding: 16 } }}
            style={metricCardStyle}
        >
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text type="secondary">{label}</Text>
                <Text strong style={{ fontSize: 24, lineHeight: 1.2 }}>
                    {value}
                    {suffix && (
                        <Text type="secondary" style={{ marginLeft: 4 }}>
                            {suffix}
                        </Text>
                    )}
                </Text>
            </Space>
        </Card>
    )

    if (!editable) {
        return (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Card
                    title={t('detail.imagePreview')}
                    styles={{
                        body: {
                            padding: 0,
                            overflow: 'hidden',
                            background: '#f6f8fb',
                        },
                    }}
                >
                    {record.media_expired ? (
                        <div style={{ padding: '56px 16px' }}>
                            <Empty description={imagePreviewEmptyDescription} />
                        </div>
                    ) : showBasicBoxOnlyPreview ? (
                        <div
                            style={{
                                minHeight: 220,
                                maxHeight: 'min(46vh, 480px)',
                                padding: 16,
                            }}
                        >
                            <ReadonlyDetectionOverlayImage
                                imageSrc={result.source_image}
                                predictions={predictions}
                                alt={t('detail.imagePreview')}
                                height="calc(min(46vh, 480px) - 32px)"
                                minHeight={188}
                                borderRadius={6}
                                preview
                                previewTitle={t('detail.imagePreview')}
                                strokeColor={BASIC_DEVICE_BOX_COLOR}
                                colorizePredictions={false}
                            />
                        </div>
                    ) : showReadonlyOverlay ? (
                        <DetectionAnnotationEditor
                            imageSrc={sourceImage}
                            fallbackImageSrc={fallbackImage}
                            filename={record.filename}
                            labelOptions={readonlyDisplayLabelOptions}
                            seedPredictions={predictions}
                            seedKey={`${record.id}-readonly-effective`}
                            editable={false}
                            dirty={false}
                            readonlyAnnotationStyle="model"
                            showAnnotationLabels={showAnnotationLabelsOnBoxes}
                            containReadonlyPreview={readonlyPreviewOnImageClick}
                            readonlyPreviewOnImageClick={
                                readonlyPreviewOnImageClick
                            }
                            onChange={() => undefined}
                            onReset={() => undefined}
                        />
                    ) : readonlyAnnotatedImage && !annotatedImageLoadFailed ? (
                        <div
                            style={{
                                minHeight: 220,
                                maxHeight: 'min(46vh, 480px)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                padding: 16,
                            }}
                        >
                            <Image
                                key={readonlyAnnotatedImage}
                                src={getImageUrl(readonlyAnnotatedImage)}
                                alt={t('detail.imagePreview')}
                                preview={{
                                    mask: (
                                        <ImagePreviewMaskContent
                                            label={t('detail.imagePreview')}
                                        />
                                    ),
                                }}
                                onLoad={() =>
                                    setLoadedReadonlyImage(
                                        readonlyAnnotatedImage,
                                    )
                                }
                                onError={() =>
                                    setAnnotatedImageLoadFailed(true)
                                }
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: 'calc(min(46vh, 480px) - 32px)',
                                    objectFit: 'contain',
                                    display: 'block',
                                    visibility:
                                        loadedReadonlyImage ===
                                        readonlyAnnotatedImage
                                            ? 'visible'
                                            : 'hidden',
                                }}
                            />
                        </div>
                    ) : (
                        <div style={{ padding: '56px 16px' }}>
                            <Empty description={imagePreviewEmptyDescription} />
                        </div>
                    )}
                </Card>
                {showReadonlyDetectionInfo ? (
                    <Card title={t('detail.detectionInfo')}>
                        <Descriptions
                            bordered
                            size="small"
                            column={{ xs: 1, sm: 1, md: 3 }}
                            style={{ marginBottom: 16 }}
                        >
                            <Descriptions.Item label={t('detail.deviceName')}>
                                {deviceLabel}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t('detail.deviceAddress')}
                            >
                                {regionPathLabel}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t('detail.detectionTime')}
                            >
                                {formatDate(record.timestamp)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('detail.fileName')}>
                                {record.filename}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('detail.deviceCode')}>
                                {record.device_id || '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                            <Col span={24} md={12}>
                                {renderMetricCard({
                                    label: t('detail.targetCount'),
                                    value: metricTargetCount,
                                    suffix: metricTargetUnit,
                                })}
                            </Col>
                            <Col span={24} md={12}>
                                {renderMetricCard({
                                    label: t('detail.durationMetric'),
                                    value: metricDuration,
                                    suffix: metricDurationUnit,
                                })}
                            </Col>
                        </Row>
                    </Card>
                ) : null}
            </Space>
        )
    }

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {record.media_expired ? (
                    <div style={{ padding: '56px 16px' }}>
                        <Empty description={t('detail.imagePreviewExpired')} />
                    </div>
                ) : (
                    <DetectionAnnotationEditor
                        imageSrc={sourceImage}
                        fallbackImageSrc={fallbackImage}
                        filename={record.filename}
                        labelOptions={annotationEditorLabelOptions}
                        seedPredictions={
                            useManualRecognitionTypes
                                ? projectPredictionsToManualRecognitionTypes(
                                      annotationSeedPredictions,
                                      readonlyManualTypeOptions,
                                      displayLanguage,
                                  )
                                : annotationSeedPredictions
                        }
                        referencePredictions={
                            useManualRecognitionTypes
                                ? projectPredictionsToManualRecognitionTypes(
                                      referencePredictions,
                                      readonlyManualTypeOptions,
                                      displayLanguage,
                                  )
                                : referencePredictions
                        }
                        seedKey={annotationSeedKey}
                        editable={editable}
                        dirty={annotationDirty}
                        actionPlacement={annotationActionPlacement}
                        zoomControlPlacement={zoomControlPlacement}
                        defaultPredictionConfidence={
                            defaultPredictionConfidence
                        }
                        showAnnotationLabels={showAnnotationLabelsOnBoxes}
                        onChange={onPredictionsChange}
                        onFooterActionsChange={onAnnotationFooterActionsChange}
                        onReset={onResetPredictions}
                    />
                )}
            </Space>
        </Space>
    )
}
